import { ArrowBack } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Container,
    IconButton,
    LinearProgress,
    Typography
} from '@mui/material';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from 'src/allauth/auth';
import AthleteDetailView from 'src/components/AthleteDetailView';
import DailyMoodCheckInButton from 'src/components/DailyMoodCheckInButton';
import UserPointsTotal from 'src/components/UserPointsTotal';
import { useFocusedAssignment } from '../context/FocusedAssignmentContext';
import { AthletePaymentAssignment } from '../object-actions/types/types';

const ProgressReport: React.FC = () => {
    const location = useLocation();
    const user = useUser();
    const { assignment, loading, error } = useFocusedAssignment();
    const [checkoutSuccess, setCheckoutSuccess] = useState<string | null>(
        () => (location.state as { checkoutSuccess?: string } | null)?.checkoutSuccess ?? null,
    );

    const assignmentFromState = location.state?.assignment as AthletePaymentAssignment | undefined;
    const displayAssignment = assignment ?? assignmentFromState ?? null;

    if (loading && !displayAssignment) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <LinearProgress />
            </Container>
        );
    }

    if (error && !displayAssignment) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button component={Link} to="/dashboard" startIcon={<ArrowBack />}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    if (!displayAssignment) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No assignment found.
                </Alert>
                <Button component={Link} to="/dashboard" startIcon={<ArrowBack />}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    return (
        <Box p={2} data-testid="program-progress-page" data-attr="progress-report">
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, alignContent: 'center', justifyItems: 'center' }}>
                <IconButton
                    component={Link}
                    to="/dashboard"
                    color="secondary"
                >
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
                    {displayAssignment.athlete?.id == user?.id
                        ? 'My Progress'
                        : `Program Progress: ${displayAssignment.athlete?.str ?? 'Assignment'}`}
                </Typography>

                {displayAssignment.athlete?.id == user?.id && <React.Fragment>
                    <DailyMoodCheckInButton />
                    <UserPointsTotal />
                </React.Fragment>}
            </Box>

            {checkoutSuccess && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => setCheckoutSuccess(null)}
                    data-testid="checkout-success-notice"
                >
                    Purchase complete — {checkoutSuccess} is now unlocked.
                </Alert>
            )}

            <AthleteDetailView assignment={displayAssignment} />
        </Box>
    );
};

export default ProgressReport;
